// alternative-components/compactWidgetsCardDetails/CompactWidgetsCardDetails.tsx
import SummaryCard from "./SummaryCard";
import { useCountry } from "@/app/services/context/CountryContext";
import { gxDashboardBalanceAccountsApi } from "@/app/apiConfig/services/customerAbstractApiLayer";
import { useQuery } from "@tanstack/react-query";
import { gxAccountBalanceTotalApi } from "@/app/apiConfig/services/accountsAbstractApiLayer";
import { useMemo } from "react";
import { buildCompactSummaryCards } from "../../mappers/compactSummaryCardsMapper";
import "../approval/styles/ApprovalRequiredPanel.scss";

interface CompactWidgetsCardDetailsProps {
  title?: string;
}

const CompactWidgetsCardDetails = ({
  title,
}: CompactWidgetsCardDetailsProps) => {
  const { selectedCountry } = useCountry();

  const currencyCode = selectedCountry?.currency?.code ?? "";

  const { data: balanceData, isLoading: loadingBalance } = useQuery({
    queryKey: ["balance", currencyCode],
    queryFn: async () => {
      const response = await gxAccountBalanceTotalApi({
        currencyCode,
      });

      if (response.status === 1) return response.data;

      throw new Error(response.errorMessage);
    },
    enabled: !!currencyCode,
  });

  const { data: dashboardBalanceData, isLoading: loadingCashFlow } = useQuery({
    queryKey: ["dashboardBalanceAccounts", currencyCode],
    queryFn: async () => {
      const response = await gxDashboardBalanceAccountsApi({
        currencyCode,
      });

      if (response.status === 1) return response.data;

      throw new Error(response.errorMessage);
    },
    enabled: !!currencyCode,
  });

  const currencyLabel =
    balanceData?.totalbalance?.currency ??
    selectedCountry?.currency?.code ??
    "AED";

  const cards = useMemo(() => {
    return buildCompactSummaryCards(
      balanceData,
      dashboardBalanceData,
      currencyLabel,
    );
  }, [balanceData, dashboardBalanceData, currencyLabel]);

  const isLoading = loadingBalance || loadingCashFlow;

  if (isLoading) {
    return (
      <div className="dashboard-container">
        <div className="alter-grid-payable">
          {["skeleton-1", "skeleton-2", "skeleton-3", "skeleton-4"].map(
            (key) => (
              <div
                key={key}
                className="gx-alter-card gx-alter-card--skeleton"
                aria-hidden="true"
              />
            ),
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {title && <h2 className="gx-h4-b">{title}</h2>}
      <div className="alter-grid-payable">
        {cards.map((item) => (
          <SummaryCard key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
};

export default CompactWidgetsCardDetails;
